import { getDateInputValueInTimeZone, getHourInTimeZone } from "../../../utils/date";
import { slotForHour } from "../../wod/utils/programming";
import type { AdminMembership } from "../../memberships/services/membership";

export const ADMIN_TIME_ZONE = "Europe/London";

export function adminToday(now = new Date()) {
  return getDateInputValueInTimeZone(now, ADMIN_TIME_ZONE);
}

export function isDateKey(value: string | null | undefined): value is string {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const parsed = new Date(`${value}T12:00:00Z`);
  return Number.isFinite(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
}

export function shiftDate(date: string, days: number) {
  const result = new Date(`${date}T12:00:00Z`);
  result.setUTCDate(result.getUTCDate() + days);
  return result.toISOString().slice(0, 10);
}

export function weekOf(date: string) {
  const day = new Date(`${date}T12:00:00Z`).getUTCDay();
  return shiftDate(date, -((day + 6) % 7));
}

/** Resolve London midnight without assuming every local day lasts 24 hours. */
export function londonMidnight(date: string) {
  if (!isDateKey(date)) throw new Error("Choose a valid date.");
  const target = Date.parse(`${date}T00:00:00Z`);
  let result = target;
  for (let attempt = 0; attempt < 3; attempt += 1) {
    const instant = new Date(result);
    const localDate = adminToday(instant);
    const localHour = getHourInTimeZone(instant, ADMIN_TIME_ZONE);
    const localAsUtc = Date.parse(`${localDate}T00:00:00Z`) + localHour * 3_600_000;
    result -= localAsUtc - target;
  }
  return new Date(result);
}

export function formatAdminDate(date: string, full = false) {
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: "UTC", weekday: full ? "long" : "short", day: "numeric", month: full ? "long" : "short",
  }).format(new Date(`${date}T12:00:00Z`));
}

export function formatAdminTime(date: Date) {
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: ADMIN_TIME_ZONE, hour: "2-digit", minute: "2-digit", hourCycle: "h23",
  }).format(date);
}

export function programmingLink(start: Date) {
  const date = adminToday(start);
  const slot = slotForHour(getHourInTimeZone(start, ADMIN_TIME_ZONE));
  return `/admin/programming?date=${date}&slot=${slot}`;
}

export function isCompletedAdminRemoval(membership: AdminMembership): boolean {
  return membership.adminChange?.action === "remove" &&
    membership.adminChange.status === "applied" &&
    membership.cancellationRequestStatus === "applied";
}

export function membershipNeedsAttention(membership: AdminMembership): boolean {
  return membership.adminChange?.status === "manual_review" || Boolean(membership.adminChange?.error) ||
    membership.state === "past_due_grace" ||
    membership.state === "past_due_suspended" ||
    membership.state === "disputed" || membership.disputeOpen ||
    (membership.accessRevoked && !isCompletedAdminRemoval(membership)) ||
    membership.providerContractStatus === "manual_review" ||
    Boolean(membership.providerContractError) ||
    membership.confirmationEmailStatus === "dead_letter" ||
    membership.confirmationEmailStatus === "manual_review" ||
    Boolean(membership.confirmationEmailError) ||
    membership.cancellationRequestStatus === "manual_review" ||
    Boolean(membership.cancellationRequestError) ||
    membership.refundReviewRequired === true ||
    membership.cancellationAcknowledgementStatus === "dead_letter" ||
    membership.cancellationAcknowledgementStatus === "manual_review" ||
    Boolean(membership.cancellationAcknowledgementError) ||
    membership.entitlementProjectionStatus === "manual_review" ||
    Boolean(membership.entitlementProjectionError) ||
    (membership.state !== "scheduled" && membership.grantsAlphaWodAccess &&
      membership.entitlementTargetUid !== null &&
      membership.entitlementProjectionStatus !== "applied") ||
    (membership.state !== "scheduled" && membership.grantsAlphaWodAccess &&
      membership.entitlementTargetUid === null);
}

export function formatAdminMoney(pence: number) {
  return new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(pence / 100);
}
